import * as React from 'react';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { Button } from '../../../../../components/Button/Button';
import '../ContactChangeForm.scss';
import { checkAvailability } from '../../../../../helpers/service/public/register';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleExclamation } from '@fortawesome/free-solid-svg-icons';

interface Props {
  email: string;
  onSubmit: (data: any) => void;
  onCancel: () => void;
  cooldownSec?: number;
  formatMmSs?: (s: number) => string;
  actionError?: string;
  cooldownCode?: string;
  onCooldownFinished?: () => void;
}

const EmailSchema = Yup.object().shape({
  email: Yup.string()
    .required('Required')
    .email('Must be valid email')
    .test('email', 'Duplicate email', value =>
      checkAvailability({ email: value }).then(res => {
        return res.isAvailable;
      })
    )
});

function CooldownInlineAlert(props: {
  text: string;
  code?: string;
  cooldownSec: number;
  formatMmSs?: (s: number) => string;
  onCooldownFinished?: () => void;
}) {
  const { text, code, cooldownSec, formatMmSs, onCooldownFinished } = props;
  const [sec, setSec] = React.useState<number>(Math.max(0, Math.ceil(cooldownSec)));

  React.useEffect(() => {
    setSec(Math.max(0, Math.ceil(cooldownSec)));
  }, [cooldownSec]);

  React.useEffect(() => {
    if (sec <= 0) return;

    const id = window.setInterval(() => {
      setSec(prev => {
        const next = prev - 1;
        if (next <= 0) {
          window.clearInterval(id);
          onCooldownFinished && onCooldownFinished();
          return 0;
        }
        return next;
      });
    }, 1000);

    return () => window.clearInterval(id);
  }, [sec]);

  const isBlocked = code === 'AUTO_BLOCKED';
  const title = isBlocked ? 'Temporarily unavailable' : 'Please wait a moment';

  return (
    <div className={`eModalCooldownAlert ${isBlocked ? 'mBlocked' : 'mRateLimited'}`}>
      <div className="eModalCooldownAlertLeft">
        <div className="eModalCooldownAlertIcon">
          <FontAwesomeIcon icon={faCircleExclamation} />
        </div>
        <div className="eModalCooldownAlertText">
          <div className="eModalCooldownAlertTitle">{title}</div>
          <div className="eModalCooldownAlertMessage">{text}</div>
        </div>
      </div>

      <div className="eModalCooldownAlertTimer">{formatMmSs ? formatMmSs(sec) : `${sec}s`}</div>
    </div>
  );
}

export function EmailChangeForm(props: Props) {
  const {
    onSubmit,
    onCancel,
    email,

    cooldownSec,
    formatMmSs,
    actionError,
    cooldownCode,
    onCooldownFinished
  } = props;

  const isCooldown = (cooldownSec || 0) > 0;

  const turnstileTokenRef = React.useRef<string | undefined>(undefined);
  const turnstileContainerRef = React.useRef<HTMLDivElement>(null);
  const turnstileWidgetIdRef = React.useRef<string | undefined>(undefined);
  const [captchaReady, setCaptchaReady] = React.useState(false);

  React.useEffect(() => {
    const sitekey = (window as any).turnstileSiteKey;
    if (!sitekey || !turnstileContainerRef.current) return;

    const renderWidget = () => {
      if (!(window as any).turnstile || turnstileWidgetIdRef.current !== undefined) return;
      turnstileWidgetIdRef.current = (window as any).turnstile.render(turnstileContainerRef.current, {
        sitekey,
        callback: (token: string) => {
          turnstileTokenRef.current = token;
          setCaptchaReady(true);
        },
        'expired-callback': () => {
          turnstileTokenRef.current = undefined;
          setCaptchaReady(false);
        },
        'error-callback': () => {
          turnstileTokenRef.current = undefined;
          setCaptchaReady(false);
        }
      });
    };

    if ((window as any).turnstile) renderWidget();
    else (window as any).__pendingTurnstileInit = renderWidget;

    return () => {
      if ((window as any).__pendingTurnstileInit === renderWidget) (window as any).__pendingTurnstileInit = undefined;
      if (turnstileWidgetIdRef.current !== undefined && (window as any).turnstile) {
        (window as any).turnstile.remove(turnstileWidgetIdRef.current);
        turnstileWidgetIdRef.current = undefined;
      }
    };
  }, []);

  return (
    <Formik
      initialValues={{ email }}
      validationSchema={EmailSchema}
      validateOnBlur={false}
      validateOnChange={false}
      onSubmit={values => {
        if (isCooldown) return;
        const captchaToken = turnstileTokenRef.current;
        turnstileTokenRef.current = undefined;
        setCaptchaReady(false);
        onSubmit({ ...values, captchaToken });
        if (turnstileWidgetIdRef.current !== undefined) {
          (window as any).turnstile?.reset(turnstileWidgetIdRef.current);
        }
      }}
    >
      {({ handleSubmit, touched, errors }) => (
        <Form className="eChangeModalForm">
          <div className="eChangeModalHeader">
            <div className="eChangeModalTitle">Update email address</div>
            <div className="eChangeModalSubtitle">We’ll send a new verification code to your updated email.</div>
          </div>

          {actionError && isCooldown && (
            <CooldownInlineAlert
              text={actionError}
              code={cooldownCode}
              cooldownSec={cooldownSec || 0}
              formatMmSs={formatMmSs}
              onCooldownFinished={onCooldownFinished}
            />
          )}

          <div className="form-group eChangeModalField">
            <label className="eChangeModalLabel">Email</label>
            <Field name="email" className="form-control mb-2" />

            {touched.email && errors.email && <div className="eChangeModalError">{errors.email}</div>}

            {isCooldown && (
              <div className="eChangeModalHint">
                You can try again in <strong>{formatMmSs ? formatMmSs(cooldownSec || 0) : `${cooldownSec}s`}</strong>.
              </div>
            )}
          </div>

          <div ref={turnstileContainerRef} className="eChangeTurnstile mt-3" />

          <div className="eChangeModalActions">
            <Button onClick={onCancel} text={'Cancel'} customClass={'btn-secondary'} type={'button'} />
            <Button
              onClick={() => {}}
              text={'Save'}
              customClass={'btn-primary'}
              disabled={isCooldown || !captchaReady}
              type={'submit'}
            />
          </div>
        </Form>
      )}
    </Formik>
  );
}
